import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import { brandColors } from '@liminal/brand/palette';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { Icon } from '@liminal/web-ui/icons/Icon';
import type { IconName } from '@liminal/web-ui/icons/registry';
import { CHECK_IN_EVENT_CODE } from '@/features/events/checkInEvent';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    position: 'fixed',
    top: '15%',
    margin: 0,
    width: '100%',
    maxWidth: 560,
    borderRadius: 12,
  },
});

const SearchField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    fontSize: 16,
    '& fieldset': { border: 'none' },
  },
});

const ResultsList = styled(List)(({ theme }) => ({
  maxHeight: 360,
  overflow: 'auto',
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: theme.palette.text.disabled,
  padding: theme.spacing(1, 2, 0.5),
}));

const ShortcutHint = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 4,
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const Kbd = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 4px',
  fontSize: 11,
  fontFamily: FONT_STACK_BODY,
  borderRadius: 4,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
}));

const CLOSED = { open: false, query: '' };

/**
 * Pages reached by name rather than by a record: a query that appears in an
 * entry's label, detail or words lists it. The event check-in opens the
 * current event's door (LIM-4707).
 */
const PAGES: readonly { label: string; detail: string; path: string; icon: IconName; words: string }[] = [
  {
    label: 'Event check-in',
    detail: CHECK_IN_EVENT_CODE,
    path: '/events',
    icon: 'calendar',
    words: 'event checkin door scan guests drink tickets issuances',
  },
];

function matchingPages(query: string) {
  const needle = query.trim().toLowerCase();
  return PAGES.filter((page) => `${page.label} ${page.detail} ${page.words}`.toLowerCase().includes(needle));
}

export function CommandPalette() {
  // One piece of state: closing the palette is also what clears its query.
  const [{ open, query }, setPalette] = useState(CLOSED);
  const close = useCallback(() => setPalette(CLOSED), []);
  const setQuery = useCallback((next: string) => setPalette((palette) => ({ ...palette, query: next })), []);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  // Keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPalette((palette) => (palette.open ? CLOSED : { open: true, query: '' }));
      }
      if (e.key === 'Escape') {
        setPalette(CLOSED);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Search users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['commandPalette', 'users', debouncedQuery],
    queryFn: () => adminClient.searchUsers({ query: debouncedQuery, limit: 5, offset: 0 }),
    enabled: open && debouncedQuery.length >= 2,
  });

  // Search transactions (if query looks like a UUID)
  const isUuidLike = debouncedQuery.length >= 8 && /^[0-9a-f-]+$/i.test(debouncedQuery);
  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: ['commandPalette', 'transactions', debouncedQuery],
    queryFn: () => adminClient.listTransactions({ accountId: debouncedQuery, pageSize: 3 }),
    enabled: open && isUuidLike,
  });

  const isLoading = usersLoading || txLoading;

  const handleSelect = useCallback((path: string) => {
    close();
    navigate(path);
  }, [close, navigate]);

  const pages = matchingPages(debouncedQuery);
  const users = usersData?.profiles ?? [];
  const transactions = txData?.transactions ?? [];
  const hasResults = pages.length > 0 || users.length > 0 || transactions.length > 0;

  return (
    <StyledDialog open={open} onClose={close}>
      <SearchField
        inputRef={inputRef}
        fullWidth
        placeholder="Search users, transactions, pages..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                {isLoading
                  ? <LoadingSpinner variant="inline" />
                  : <Icon name="search" size={20} color={brandColors.greyLight} />
                }
              </InputAdornment>
            ),
          },
        }}
      />

      {debouncedQuery.length >= 2 && (
        <ResultsList>
          {pages.length > 0 && (
            <>
              <CategoryLabel>Pages</CategoryLabel>
              {pages.map(page => (
                <ListItemButton
                  key={page.path}
                  onClick={() => handleSelect(page.path)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon name={page.icon} size={18} />
                  </ListItemIcon>
                  <ListItemText
                    primary={page.label}
                    secondary={page.detail}
                    primaryTypographyProps={{ fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItemButton>
              ))}
            </>
          )}

          {users.length > 0 && (
            <>
              <CategoryLabel>Users</CategoryLabel>
              {users.map(ep => {
                const user = ep.profile;
                if (!user) return null;
                return (
                  <ListItemButton
                    key={user.userId}
                    onClick={() => handleSelect(`/users/${user.userId}`)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Icon name="person" size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email}
                      secondary={[user.email, user.displayTag ? `@${user.displayTag}` : null].filter(Boolean).join(' \u2022 ')}
                      primaryTypographyProps={{ fontSize: 14 }}
                      secondaryTypographyProps={{ fontSize: 12 }}
                    />
                  </ListItemButton>
                );
              })}
            </>
          )}

          {transactions.length > 0 && (
            <>
              <CategoryLabel>Transactions</CategoryLabel>
              {transactions.map(tx => (
                <ListItemButton
                  key={tx.id}
                  onClick={() => handleSelect(`/transactions`)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon name="swap" size={18} />
                  </ListItemIcon>
                  <ListItemText
                    primary={tx.id?.slice(0, 8) ?? 'Transaction'}
                    secondary={tx.context?.replace('TRANSACTION_CONTEXT_', '') ?? ''}
                    primaryTypographyProps={{ fontSize: 14, fontFamily: FONT_STACK_BODY }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItemButton>
              ))}
            </>
          )}

          {!hasResults && !isLoading && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography color="text.disabled" variant="body2">
                No results found
              </Typography>
            </Box>
          )}
        </ResultsList>
      )}

      <ShortcutHint>
        <Kbd>{'\u2318'}K</Kbd>
        <Typography variant="caption" color="text.disabled">to toggle</Typography>
        <Box sx={{ flex: 1 }} />
        <Kbd>Esc</Kbd>
        <Typography variant="caption" color="text.disabled">to close</Typography>
      </ShortcutHint>
    </StyledDialog>
  );
}
